// src/pages/ContentApprovalsPage.js
//
// Triage/queue view at /social/approvals. This page intentionally has NO
// approve/reject buttons of its own — it only lists tasks in the relevant
// statuses and links into the content task detail page
// (/social/tasks/:taskId, built by another agent) where the actual
// approve/reject actions live.
//
// Three sections, per spec:
//   1. Needs Internal Review        -> status === 'Internal Review'
//   2. Ready to Send to Client      -> status === 'Ready for Client Approval'
//   3. Awaiting Client Response     -> status === 'Client Approval'

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, onSnapshot, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { getPlatformLabel } from '../utils/socialEnumLabels';
import styles from '../styles/ContentApprovalsPage.module.css';

const QUEUE_STATUSES = ['Internal Review', 'Ready for Client Approval', 'Client Approval'];

const TRANSLATIONS = {
  en: {
    pageTitle: 'Content Approvals Queue',
    loading: 'Loading approvals queue...',
    needsInternalReview: 'Needs Internal Review',
    needsInternalReviewHint: "Tasks awaiting an internal reviewer's approve/reject decision.",
    needsInternalReviewEmpty: 'Nothing currently needs internal review.',
    readyToSend: 'Ready to Send to Client',
    readyToSendHint: 'Internally approved tasks that still need to be sent to the client (status flipped to "Client Approval").',
    readyToSendEmpty: 'Nothing is currently ready to send to a client.',
    awaitingClient: 'Awaiting Client Response',
    awaitingClientHint: 'Tasks sent to the client, waiting for their approval or feedback.',
    awaitingClientEmpty: 'No tasks are currently awaiting client response.',
    colClient: 'Client',
    colTitle: 'Title',
    colPlatform: 'Platform',
    colWriter: 'Writer',
    colDesigner: 'Designer',
    colReviewer: 'Reviewer',
    colDueDate: 'Due Date',
    colAction: 'Action',
    viewAct: 'View / Act',
    untitled: 'Untitled',
    dash: '—',
  },
  ar: {
    pageTitle: 'قائمة موافقات المحتوى',
    loading: 'جارٍ تحميل قائمة الموافقات...',
    needsInternalReview: 'بحاجة لمراجعة داخلية',
    needsInternalReviewHint: 'مهام بانتظار قرار الموافقة أو الرفض من مراجع داخلي.',
    needsInternalReviewEmpty: 'لا توجد مهام تحتاج إلى مراجعة داخلية حالياً.',
    readyToSend: 'جاهز للإرسال إلى العميل',
    readyToSendHint: 'مهام تمت الموافقة عليها داخلياً وما زالت بحاجة لإرسالها إلى العميل (تغيرت حالتها إلى "موافقة العميل").',
    readyToSendEmpty: 'لا توجد مهام جاهزة للإرسال إلى عميل حالياً.',
    awaitingClient: 'بانتظار رد العميل',
    awaitingClientHint: 'مهام تم إرسالها إلى العميل، بانتظار موافقته أو ملاحظاته.',
    awaitingClientEmpty: 'لا توجد مهام بانتظار رد العميل حالياً.',
    colClient: 'العميل',
    colTitle: 'العنوان',
    colPlatform: 'المنصة',
    colWriter: 'الكاتب',
    colDesigner: 'المصمم',
    colReviewer: 'المراجع',
    colDueDate: 'تاريخ الاستحقاق',
    colAction: 'الإجراء',
    viewAct: 'عرض / تنفيذ إجراء',
    untitled: 'بدون عنوان',
    dash: '—',
  },
};

const parseTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const ContentApprovalsPage = () => {
  const { language, toggleLanguage, isRTL } = useSocialLanguage();
  const t = TRANSLATIONS[language];
  const [tasks, setTasks] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // One-time fetch of users so we can resolve uids -> display names.
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersCol = collection(db, 'users');
        const snapshot = await getDocs(usersCol);
        const map = {};
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          map[docSnap.id] = data.name || data.email || docSnap.id;
        });
        setUsersMap(map);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchUsers();
  }, []);

  // Live queue via onSnapshot, filtered with a where('status','in',[...]) query.
  useEffect(() => {
    setLoading(true);
    setError('');
    const tasksCol = collection(db, 'contentTasks');
    const q = query(tasksCol, where('status', 'in', QUEUE_STATUSES));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetched = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            dueDate: parseTimestamp(data.dueDate),
          };
        });
        setTasks(fetched);
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to approvals queue:', err);
        setError('Failed to load approvals queue.');
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const resolveName = (uid) => (uid ? usersMap[uid] || uid : t.dash);

  const needsInternalReview = useMemo(
    () => tasks.filter((t) => t.status === 'Internal Review'),
    [tasks]
  );
  const readyToSend = useMemo(
    () => tasks.filter((t) => t.status === 'Ready for Client Approval'),
    [tasks]
  );
  const awaitingClient = useMemo(
    () => tasks.filter((t) => t.status === 'Client Approval'),
    [tasks]
  );

  const renderTable = (rows, emptyMessage) => {
    if (rows.length === 0) {
      return <p className={styles.noDataMessage}>{emptyMessage}</p>;
    }
    return (
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t.colClient}</th>
              <th>{t.colTitle}</th>
              <th>{t.colPlatform}</th>
              <th>{t.colWriter}</th>
              <th>{t.colDesigner}</th>
              <th>{t.colReviewer}</th>
              <th>{t.colDueDate}</th>
              <th>{t.colAction}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((task) => (
              <tr key={task.id}>
                <td>{task.clientName || t.dash}</td>
                <td>{task.title || t.untitled}</td>
                <td>{task.platform ? getPlatformLabel(task.platform, language) : t.dash}</td>
                <td>{resolveName(task.writerId)}</td>
                <td>{resolveName(task.designerId)}</td>
                <td>{resolveName(task.reviewerId)}</td>
                <td>{formatDate(task.dueDate)}</td>
                <td>
                  <Link to={`/social/tasks/${task.id}`} className={styles.viewLink}>
                    {t.viewAct}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={styles.header}>
        <h2 className={styles.pageTitle}>{t.pageTitle}</h2>
        <LanguageToggle language={language} onToggle={toggleLanguage} />
      </div>

      {error && <p className={styles.errorMessage}>{error}</p>}

      {loading ? (
        <div className={styles.loadingIndicator}>{t.loading}</div>
      ) : (
        <>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              {t.needsInternalReview} <span className={styles.countBadge}>{needsInternalReview.length}</span>
            </h3>
            <p className={styles.sectionHint}>{t.needsInternalReviewHint}</p>
            {renderTable(needsInternalReview, t.needsInternalReviewEmpty)}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              {t.readyToSend} <span className={styles.countBadge}>{readyToSend.length}</span>
            </h3>
            <p className={styles.sectionHint}>{t.readyToSendHint}</p>
            {renderTable(readyToSend, t.readyToSendEmpty)}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              {t.awaitingClient} <span className={styles.countBadge}>{awaitingClient.length}</span>
            </h3>
            <p className={styles.sectionHint}>{t.awaitingClientHint}</p>
            {renderTable(awaitingClient, t.awaitingClientEmpty)}
          </section>
        </>
      )}
    </div>
  );
};

export default ContentApprovalsPage;
